import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"

// Canonical button. All variants are rounded-lg and use the --color-* token
// family that the rest of the app uses, so this can stand in for the raw
// <button> + inline-style pattern without visual drift.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-[var(--color-primary-600)] text-white hover:bg-[var(--color-primary-700)]",
        secondary:
          "bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] hover:bg-[color-mix(in_srgb,var(--color-text-primary)_8%,var(--color-bg-secondary))]",
        outline:
          "bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] border border-[var(--color-border-primary)] hover:bg-[var(--color-bg-secondary)]",
        ghost:
          "bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]",
        destructive:
          "bg-[var(--color-error)] text-white hover:brightness-95",
        success:
          "bg-[var(--color-success)] text-white hover:brightness-95",
        warning:
          "bg-[var(--color-warning)] text-white hover:brightness-95",
        link:
          "h-auto px-0 text-[var(--color-primary-600)] underline-offset-4 hover:underline",
      },
      size: {
        // The default sizes (md, icon) hit the 44px WCAG/iOS touch
        // target. Keep xs/sm/icon-xs/icon-sm as explicit opt-outs for
        // compact-density UIs (filter pills, dense tables) — callers
        // that pick them are taking responsibility for hit area.
        xs: "h-7 px-2.5 text-xs [&_svg]:size-3.5",
        sm: "h-8 px-3 text-xs [&_svg]:size-3.5",
        md: "h-11 px-4 text-sm [&_svg]:size-4",
        lg: "h-11 px-5 text-sm [&_svg]:size-4",
        "icon-xs": "h-7 w-7 [&_svg]:size-3.5",
        "icon-sm": "h-8 w-8 [&_svg]:size-4",
        icon: "h-11 w-11 [&_svg]:size-4",
        "icon-lg": "h-11 w-11 [&_svg]:size-5",
      },
      block: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      block: false,
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      block,
      asChild = false,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, block, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? <Loader2 className="animate-spin" /> : null}
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
